import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { CourseService } from '../../services/course/course.service';
import { UnifiedCartService } from '../../services/unified-cart/unified-cart.service';
import { ActivatedRoute, Router } from '@angular/router';
import { availableClusterOffers, cartSubtotal, clusterSaving } from '../../core/cluster-pricing';
import { CourseModel, CourseUnitModel, TagModel } from 'src/app/core/models';

@Component({
  selector: 'app-course-list',
  templateUrl: './course-list.component.html',
  styleUrls: ['./course-list.component.scss']
})
export class CourseListComponent implements OnInit, OnDestroy {
  courses: CourseModel[] = [];
  tags: TagModel[] = [];

  categoryCards: any[] = [];

  selectedTagSlug = '';
  selectedCategoryName = '';
  requestedCategoryName = '';
  selectedLocation = '';
  searchText = '';

  // Public long-course catalogue filters.
  selectedFilterCourseId: number | null = null;
  courseOptions: Array<{ courseId: number; courseCode: string; courseName: string; qualificationType: string }> = [];
  catalogueFirst = 0;
  cataloguePageSize = 8;
  catalogueTotalRecords = 0;
  isCatalogueLoading = false;

  /** Selected qualification/certificate level. Empty means all levels. */
  selectedQualificationType = '';

  /**
   * Complete public catalogue used to build qualification options and to decide
   * which category cards are relevant to a selected qualification.
   */
  private qualificationCatalogue: CourseModel[] = [];

  first = 0;
  pageSize = 5;
  totalRecords = 0;

  selectedCourse: CourseModel | null = null;
  cartCount = 0;
  selectingOccurrenceCourse: CourseModel | null = null;
  cartItems: any[] = [];
  message = '';
  addingCourseId: number | null = null;
  offersLoading = new Set<number>();
  offersErrors = new Set<number>();
  private loadedOffers = new Map<number, CourseModel[]>();
  private offerSubscriptions = new Subscription();
  private cartSubscription?: Subscription;
  offerSaving = clusterSaving;

  displayState: 'CATEGORIES' | 'COURSES' | 'COURSES_LIST' = 'CATEGORIES';

  fallbackCategories: any[] = [
  ];

  fallbackCourses: CourseModel[] = [
  ];
  readonly defaultCategoryImage = 'assets/images/categories/default-category.jpg';
  readonly defaultCourseImage = 'assets/images/default-course.jpg';
  readonly defaultCourseDescription = 'Industry focused training with practical learning, assessment and support.';

  displayPopup: boolean = false;
  popupCourse: CourseModel | null = null;
  //store form state code
  selectedQualificationStateCode: string = '';
  constructor(
    private courseService: CourseService,
    public unifiedCart: UnifiedCartService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.selectedQualificationType = (params.get('qualificationType') || '').trim();
      this.selectedQualificationStateCode = (params.get('state_code') ?? '').toUpperCase();



      this.loadCourseOptions();


      this.selectedTagSlug = (params.get('tagSlug') || '').trim();
      this.requestedCategoryName = (params.get('categoryName') || '').trim();
      this.searchText = (params.get('keyword') || '').trim();
      const courseId = Number(params.get('courseId') || 0);
      this.selectedFilterCourseId = courseId > 0 ? courseId : null;
      const requestedPageSize = Number(params.get('pageSize') || 8);
      this.cataloguePageSize = [8, 12, 24].includes(requestedPageSize) ? requestedPageSize : 8;
      this.catalogueFirst = Math.max(0, (Number(params.get('page') || 1) - 1) * this.cataloguePageSize);
      this.first = 0;

      // Deep links from Home/search return directly to the card list.
      this.displayState = (params.get('view') === 'list' || this.selectedTagSlug || this.requestedCategoryName || this.searchText || this.selectedFilterCourseId)
        ? 'COURSES_LIST'
        : 'CATEGORIES';

      this.loadPageData();

      // Category-name links from Home must first resolve the live Tag slug returned by the API.
      if (this.displayState === 'COURSES_LIST' && !this.requestedCategoryName) this.loadCataloguePage();
    });
  }

  ngOnDestroy(): void { this.offerSubscriptions.unsubscribe(); this.cartSubscription?.unsubscribe(); }

  getCategoryDisplayImage(category: any): string {
    // Return default image when category image is empty.
    if (!category || !category.imageUrl || !String(category.imageUrl).trim()) {
      return this.defaultCategoryImage;
    }

    // Return uploaded/admin category image.
    return category.imageUrl;
  }

  setDefaultCategoryImage(event: Event): void {
    // Set default image when image URL is broken.
    const imageElement = event.target as HTMLImageElement;

    if (imageElement && imageElement.src !== this.defaultCategoryImage) {
      imageElement.src = this.defaultCategoryImage;
    }
  }

  getCourseDisplayImage(course: any): string {
    // Return default course image when course is empty.
    if (!course) {
      return this.defaultCourseImage;
    }

    // Use uploaded/admin course image when available.
    if (course.imageUrl && String(course.imageUrl).trim()) {
      return course.imageUrl;
    }

    // Use course-name based fallback.
    const courseNameImage = this.getImage(course.courseName);

    // Return course-name fallback or final default image.
    return courseNameImage || this.defaultCourseImage;
  }

  setDefaultCourseImage(event: Event): void {
    // Set default image when course image URL is broken.
    const imageElement = event.target as HTMLImageElement;

    if (imageElement && imageElement.src !== this.defaultCourseImage) {
      imageElement.src = this.defaultCourseImage;
    }
  }

  /**
   * Qualification levels available in the public catalogue.
   *
   * Values are derived from actual long-course data so new qualification types
   * added through Admin appear automatically.
   */
  get qualificationTypes(): string[] {
    const standardOrder = [
      'Certificate I',
      'Certificate II',
      'Certificate III',
      'Certificate IV',
      'Diploma',
      'Advanced Diploma'
    ];

    const values = [...new Set(
      this.qualificationCatalogue
        .map(course => (course.qualificationType || '').trim())
        .filter(Boolean)
    )];

    return values.sort((a, b) => {
      const ai = standardOrder.findIndex(value => value.toLowerCase() === a.toLowerCase());
      const bi = standardOrder.findIndex(value => value.toLowerCase() === b.toLowerCase());

      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return a.localeCompare(b);
    });
  }

  /**
   * Category cards relevant to the selected qualification.
   *
   * Direct /courses access leaves qualification blank and therefore shows every
   * category. Coming from Home (Certificate II/III/IV/Diploma) shows only
   * categories containing at least one matching course.
   */
  get visibleCategoryCards(): any[] {
    if (!this.selectedQualificationType) {
      return this.categoryCards;
    }

    const matchingTagIds = new Set<number>();

    this.qualificationCatalogue
      .filter(course => this.matchesQualification(course))
      .forEach(course => {
        (course.tags || []).forEach((tag: any) => {
          const tagId = Number(tag?.tagId ?? tag?.TagId);
          if (tagId > 0) matchingTagIds.add(tagId);
        });

        (course.tagIds || []).forEach((tagId: any) => {
          const id = Number(tagId);
          if (id > 0) matchingTagIds.add(id);
        });
      });

    return this.categoryCards.filter(category =>
      !category.tagId || matchingTagIds.has(Number(category.tagId))
    );
  }

  /**
   * Public course results after search, qualification and location filtering.
   */
  get filteredCourses(): CourseModel[] {
    const text = this.searchText.toLowerCase().trim();

    return this.courses.filter(course => {
      const name = (course.courseName || '').toLowerCase();
      const description = (course.shortDescription || '').toLowerCase();
      const location = (course.locationName || '').toLowerCase();

      const isTextMatch = !text || name.includes(text) || description.includes(text);
      const isLocationMatch = !this.selectedLocation ||
        location.includes(this.selectedLocation.toLowerCase());
      const isQualificationMatch = this.matchesQualification(course);

      return isTextMatch && isLocationMatch && isQualificationMatch;
    });
  }

  /** Current client-side page after all public filters have been applied. */
  get pagedCourses(): CourseModel[] {
    return this.filteredCourses.slice(this.first, this.first + this.pageSize);
  }

  get individualCourses(): CourseModel[] {
    const individuals = new Map<number, CourseModel>();
    this.courses.forEach(course => {
      if (course.isCluster) {
        (course.clusterCourses || []).forEach(unit => {
          if (!individuals.has(unit.courseId)) individuals.set(unit.courseId, {
            shortDescription: '', fullDescription: '', startDate: null, imageUrl: '', brochureUrl: '', tags: [],
            deliveryMode: '', locationName: '', courseTimes: [], ...unit, isCluster: false
          } as CourseModel);
        });
      } else individuals.set(course.courseId, course);
    });
    return [...individuals.values()];
  }

  clusterOffers(course: CourseModel): CourseModel[] {
    /*
     * Prefer the offers returned by the API.
     *
     * The API now determines which cluster offers belong to the course.
     * Do not run the result through availableClusterOffers() again because
     * a cluster master may legitimately receive itself as the available
     * discounted cluster offer.
     */
    const apiOffers =
      this.loadedOffers.get(course.courseId) ??
      course.availableClusters ??
      [];

    if (apiOffers.length) {
      /*
       * Remove accidental duplicates only.
       * Do NOT remove an offer just because offer.courseId === course.courseId.
       */
      const uniqueOffers = new Map<number, CourseModel>();

      apiOffers.forEach(offer => {
        if (
          offer &&
          offer.courseId > 0 &&
          offer.isCluster &&
          offer.isActive !== false
        ) {
          uniqueOffers.set(
            Number(offer.courseId),
            offer
          );
        }
      });

      return [...uniqueOffers.values()];
    }

    /*
     * Fallback for older API responses that do not contain availableClusters.
     */
    return availableClusterOffers(
      course.courseId,
      this.courses.filter(item => item.isCluster)
    );
  }

  offerCourseNames(offer: CourseModel): string {
    const names: string[] = [];

    if (offer.courseName) {
      names.push(
        this.courseDisplayTitle(offer)
      );
    }

    (offer.clusterCourses || []).forEach(unit => {
      if (
        unit.courseName &&
        !names.some(
          name =>
            name.trim().toLowerCase() ===
            unit.courseName.trim().toLowerCase()
        )
      ) {
        names.push(unit.courseName);
      }
    });

    return names.join(' + ');
  }

  loadClusterOffers(course: CourseModel): void {
    if (this.offersLoading.has(course.courseId)) {
      return;
    }

    this.offersErrors.delete(course.courseId);

    /*
     * Public list already supplied cluster offers.
     */
    if (
      Array.isArray(course.availableClusters) &&
      course.availableClusters.length > 0
    ) {
      this.loadedOffers.set(
        course.courseId,
        course.availableClusters
      );

      return;
    }

    this.offersLoading.add(course.courseId);

    this.offerSubscriptions.add(
      this.courseService
        .getCourseById(course.courseId)
        .subscribe({
          next: detail => {
            this.offersLoading.delete(course.courseId);

            if (
              detail?.courseId === course.courseId &&
              Array.isArray(detail.availableClusters)
            ) {
              this.loadedOffers.set(
                course.courseId,
                detail.availableClusters
              );

              this.offersErrors.delete(
                course.courseId
              );
            } else {
              this.loadedOffers.set(
                course.courseId,
                []
              );
            }
          },

          error: () => {
            this.offersLoading.delete(
              course.courseId
            );

            this.offersErrors.add(
              course.courseId
            );
          }
        })
    );
  }

  /**
   * Adds either an individual course or a training cluster from the public list.
   *
   * The latest detail is loaded before adding so cluster membership/pricing and
   * ordinary-course availability are validated by the API response.
   */
  /**
   * Main catalogue CTA always means "add this course only".
   * A course row may also be the master of one or more discounted clusters,
   * but the customer must still be able to buy the course by itself.
   */
  addPublicCourse(course: CourseModel): void {
    if (!course?.isActive) return;
    this.selectingOccurrenceCourse = course;
  }

  get coursePrice(): number {
    return this.unifiedCart.subtotal;
  }

  get currencyCode(): string { return this.unifiedCart.items[0]?.currency || 'AUD'; }

  get gst(): number {
    return 0;
  }

  get total(): number {
    return this.coursePrice + this.gst;
  }



  loadPageData(): void {
    this.loadQualificationCatalogue();
    this.loadCategories();

    this.cartSubscription?.unsubscribe();
    this.cartSubscription = this.unifiedCart.items$.subscribe(items => {
      this.cartItems = [];
      this.cartCount = items.length;

    });
  }

  loadCategories(): void {
    this.tags = [];

    this.categoryCards = this.fallbackCategories;

    this.courseService.getTags('PUBLIC_LIST').subscribe({
      next: response => {
        if (response && response.length) {
          this.tags = [
            {
              tagId: 0,
              tagName: 'All Categories',
              slug: '',
              description: 'View all available training courses.',
              imageUrl: 'assets/images/categories/all-courses.jpg'
            },
            ...response
          ];

          this.categoryCards = response.map((tag: TagModel, index: number) => {
            return {
              tagId: tag.tagId,
              tagName: tag.tagName,
              slug: tag.slug,
              description: tag.description || this.getCategoryDescription(tag.tagName),
              imageUrl: tag.imageUrl || this.getCategoryImage(tag.tagName, index),
              icon: this.getCategoryIcon(tag.tagName),
              courseCount: 0
            };
          });

          if (this.requestedCategoryName) {
            // Home cards use a readable category name. Resolve it against the live
            // Tag list so this keeps working even when the stored slug changes.
            const requestedKey = this.normaliseCategoryKey(this.requestedCategoryName);
            const matchedTag = response.find((tag: TagModel) =>
              this.normaliseCategoryKey(tag.tagName) === requestedKey ||
              this.normaliseCategoryKey(tag.slug) === requestedKey
            );

            if (matchedTag) {
              this.selectedTagSlug = matchedTag.slug || '';
              this.selectedCategoryName = matchedTag.tagName || this.requestedCategoryName;
              this.requestedCategoryName = '';
              this.catalogueFirst = 0;
              this.displayState = 'COURSES_LIST';
              this.loadCataloguePage();
              return;
            }
          }

          if (this.selectedTagSlug) {
            this.selectedCategoryName = this.getTagNameBySlug(this.selectedTagSlug);
          }
        }
      },
      error: () => {
        this.categoryCards = this.fallbackCategories;
      }
    });
  }

  openCategory(category: any): void {
    this.selectedTagSlug = category.slug || '';
    this.selectedCategoryName = category.tagName || 'All Courses';
    this.searchText = '';
    this.selectedFilterCourseId = null;
    this.catalogueFirst = 0;
    this.displayState = 'COURSES_LIST';
    this.loadCataloguePage();
  }

  backToCategories(): void {
    this.displayState = 'CATEGORIES';
    this.message = '';
    this.selectedTagSlug = '';
    this.selectedCategoryName = '';
    this.searchText = '';
    this.selectedFilterCourseId = null;
    this.catalogueFirst = 0;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: null, tagSlug: null, categoryName: null, qualificationType: null, keyword: null, courseId: null, page: null, pageSize: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /* loadCourseOptions(): void {
    this.courseService.getPublicCourseOptions(this.selectedQualificationStateCode).subscribe({
      next: options => this.courseOptions = options || [],
      error: () => this.courseOptions = []
    });
  } */

  onCatalogueFilterChange(): void {
    this.catalogueFirst = 0;
    this.displayState = 'COURSES_LIST';
    this.loadCataloguePage();
  }

  clearCatalogueFilters(): void {
    this.selectedFilterCourseId = null;
    this.searchText = '';
    this.catalogueFirst = 0;
    this.loadCataloguePage();
  }

  onCataloguePageChange(event: any): void {

    if (this.isCatalogueLoading) {
      return;
    }

    const newPageSize = Number(event.rows) || 8;

    const pageSizeChanged =
      this.cataloguePageSize !== newPageSize;

    this.cataloguePageSize = newPageSize;

    this.catalogueFirst = pageSizeChanged
      ? 0
      : Number(event.first) || 0;

    this.loadCataloguePage();

    document.getElementById('long-course-card-list')
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
  }

  private loadCataloguePage(): void {

    if (this.isCatalogueLoading) {
      return;
    }

    this.isCatalogueLoading = true;

    const pageNo =
      Math.floor(
        this.catalogueFirst / this.cataloguePageSize
      ) + 1;

    this.courseService.getCourses(
      this.selectedTagSlug,
      pageNo,
      this.cataloguePageSize,
      this.selectedQualificationStateCode,
      {
        courseId: this.selectedFilterCourseId || 0,
        keyword: this.searchText || '',
        qualificationType: this.selectedQualificationType || ''
      }
    ).subscribe({

      next: (response) => {

        const totalRecords =
          Number(response.totalRecords || 0);

        const totalPages = Math.max(
          1,
          Math.ceil(totalRecords / this.cataloguePageSize)
        );

        if (pageNo > totalPages) {

          this.catalogueFirst =
            (totalPages - 1) * this.cataloguePageSize;

          this.isCatalogueLoading = false;

          this.loadCataloguePage();
          return;
        }

        this.courses = response.records || [];

        this.catalogueTotalRecords = totalRecords;

        this.isCatalogueLoading = false;
      },

      error: (error) => {

        console.error(
          'Unable to load qualification courses:',
          error
        );

        this.courses = [];

        this.catalogueTotalRecords = 0;

        this.isCatalogueLoading = false;
      }
    });
  }

  private updateCatalogueUrl(): void {
    const pageNo = Math.floor(this.catalogueFirst / this.cataloguePageSize) + 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        view: this.displayState === 'COURSES_LIST' ? 'list' : null,
        tagSlug: this.selectedTagSlug || null,
        qualificationType: this.selectedQualificationType || null,
        keyword: this.searchText?.trim() || null,
        courseId: this.selectedFilterCourseId || null,
        page: pageNo > 1 ? pageNo : null,
        pageSize: this.cataloguePageSize !== 8 ? this.cataloguePageSize : null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  get courseDetailQueryParams(): any {
    const pageNo = Math.floor(this.catalogueFirst / this.cataloguePageSize) + 1;
    return {
      returnView: 'list',
      tagSlug: this.selectedTagSlug || null,
      qualificationType: this.selectedQualificationType || null,
      keyword: this.searchText?.trim() || null,
      courseId: this.selectedFilterCourseId || null,
      page: pageNo > 1 ? pageNo : null,
      pageSize: this.cataloguePageSize !== 8 ? this.cataloguePageSize : null
    };
  }

  selectTag(slug: string): void {
    this.selectedTagSlug = slug;
    this.selectedCategoryName = this.getTagNameBySlug(slug);
    this.first = 0;
    this.loadCourses();
  }


  /**
   * Applies the certificate/qualification filter and keeps the URL shareable.
   * Selecting "All Qualification Types" removes the query parameter.
   */
  onQualificationTypeChange(): void {
    this.first = 0;

    // If the currently selected category has no course for the new level,
    // return to All Categories rather than presenting an empty category.
    if (this.selectedTagSlug &&
      this.selectedQualificationType &&
      !this.categoryHasQualification(this.selectedTagSlug, this.selectedQualificationType)) {
      this.selectedTagSlug = '';
      this.selectedCategoryName = 'All Courses';
      this.loadCourses();
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        qualificationType: this.selectedQualificationType || null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.pageSize = event.rows;
  }

  addToCart(course: CourseModel): boolean {
    if (!course?.isActive) return false;
    this.selectingOccurrenceCourse = course;
    return true;
  }

  /**
   * Adds only the current/master course at its original individual price.
   * A cluster master keeps the same CourseId, but public customers can still
   * choose the course individually instead of taking the complete discount offer.
   */
  addIndividualCourse(course: CourseModel): void {
    this.addPublicCourse(course);
  }

  /** Standard price shown for the standalone/current course. */
  individualCoursePrice(course: CourseModel): number {
    if (course?.isCluster && Number(course.individualPrice || 0) > 0) {
      return Number(course.individualPrice);
    }

    return Number(course?.price || 0);
  }

  /** True only when the standalone course is in the cart, not a cluster using the same CourseId. */
  isIndividualSelected(courseId: number): boolean {
    return this.unifiedCart.isSelected('QUALIFICATION', courseId);
  }

  /** True only when the discounted cluster itself is selected. */
  isClusterSelected(clusterId: number): boolean {
    return false;
  }

  bookNow(course: CourseModel): void {
    this.addPublicCourse(course);
  }

  removeFromCart(courseId: number): void {
    this.unifiedCart.remove('QUALIFICATION', courseId);
  }

  isSelected(courseId: number): boolean {
    return this.unifiedCart.isSelected('QUALIFICATION', courseId);
  }

  getImage(courseName: string): string {
    const name = (courseName || '').toLowerCase();

    if (name.includes('white')) {
      return 'assets/images/white-card.svg';
    }

    if (name.includes('height')) {
      return 'assets/images/heights.svg';
    }

    if (name.includes('excavator')) {
      return 'assets/images/excavator.svg';
    }

    if (name.includes('first')) {
      return 'assets/images/first-aid.svg';
    }

    return 'assets/images/forklift.svg';
  }

  /**
   * Loads all courses for the selected category and paginates them client-side.
   *
   * Qualification filtering after a server page was loaded could otherwise hide
   * valid Certificate III/IV courses located on a different server page.
   */
  private loadCourses(): void {
    this.offerSubscriptions.unsubscribe();
    this.offerSubscriptions = new Subscription();

    this.loadedOffers.clear();
    this.offersLoading.clear();
    this.offersErrors.clear();

    this.offerSubscriptions.add(
      this.courseService
        .getCourses(
          this.selectedTagSlug,
          1,
          8,
          this.selectedQualificationStateCode
        )
        .subscribe({
          next: response => {
            this.courses =
              response.records?.length
                ? response.records
                : this.fallbackCourses;

            this.totalRecords =
              this.filteredCourses.length;

            /*
             * Check cluster discounts for all public records.
             */
            this.courses.forEach(course =>
              this.loadClusterOffers(course)
            );
          },

          error: () => {
            this.courses =
              this.fallbackCourses;

            this.totalRecords =
              this.filteredCourses.length;
          }
        })
    );
  }

  /** Loads catalogue metadata used by qualification/category filtering. */
  private loadQualificationCatalogue(): void {
    this.courseService.getCourses('', 1, 500, this.selectedQualificationStateCode).subscribe({
      next: response => {
        this.qualificationCatalogue = response.records || [];

        // When entering from Home, stay on the category view but only show
        // categories that contain the requested qualification.
        if (this.displayState === 'COURSES') {
          this.loadCourses();
        }
      },
      error: () => {
        this.qualificationCatalogue = [];
      }
    });
  }

  /** Case-insensitive qualification comparison. */
  private matchesQualification(course: CourseModel): boolean {
    if (!this.selectedQualificationType) {
      return true;
    }

    return (course.qualificationType || '').trim().toLowerCase() ===
      this.selectedQualificationType.trim().toLowerCase();
  }

  /** Returns whether a category has at least one course for a qualification. */
  private categoryHasQualification(tagSlug: string, qualificationType: string): boolean {
    if (!tagSlug || !qualificationType) {
      return true;
    }

    const tag = this.tags.find(item => item.slug === tagSlug);
    if (!tag) {
      return false;
    }

    return this.qualificationCatalogue.some(course => {
      if ((course.qualificationType || '').trim().toLowerCase() !== qualificationType.trim().toLowerCase()) {
        return false;
      }

      const tagIds = [
        ...(course.tagIds || []).map((id: any) => Number(id)),
        ...(course.tags || []).map((item: any) => Number(item?.tagId ?? item?.TagId))
      ];

      return tagIds.includes(Number(tag.tagId));
    });
  }

  /** Compare category/tag names without being affected by spaces, slashes or punctuation. */
  private normaliseCategoryKey(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '');
  }

  private getTagNameBySlug(slug: string): string {
    if (!slug) {
      return 'All Courses';
    }

    const selectedTag = this.tags.find(tag => tag.slug === slug);

    return selectedTag ? selectedTag.tagName : 'Selected Courses';
  }

  private getCategoryDescription(name: string): string {
    const lowerName = (name || '').toLowerCase();

    if (lowerName.includes('high')) {
      return 'Obtain your High-Risk Work Licence with courses covering cranes, rigging, scaffolding, and more.';
    }

    if (lowerName.includes('construction')) {
      return 'Induction training programs to prepare students to obtain their licence to work on construction sites.';
    }

    if (lowerName.includes('emergency') || lowerName.includes('safety') || lowerName.includes('first')) {
      return 'Emergency services training courses covering first aid, fire safety, and accident response.';
    }

    if (lowerName.includes('plant') || lowerName.includes('machinery')) {
      return 'Plant and equipment training across civil construction, mining, transport and logistics industries.';
    }

    if (lowerName.includes('voc')) {
      return 'Evaluate operator skill against national standards of competency for civil construction industry.';
    }

    if (lowerName.includes('hsr')) {
      return 'Flexible Health and Safety Representative initial training and refresher courses.';
    }

    return 'Explore nationally recognised training courses with flexible delivery options.';
  }

  private getCategoryIcon(name: string): string {
    const lowerName = (name || '').toLowerCase();

    if (lowerName.includes('high')) {
      return 'fa-warning';
    }

    if (lowerName.includes('construction')) {
      return 'fa-building-o';
    }

    if (lowerName.includes('emergency') || lowerName.includes('first')) {
      return 'fa-fire-extinguisher';
    }

    if (lowerName.includes('plant') || lowerName.includes('machinery')) {
      return 'fa-truck';
    }

    if (lowerName.includes('voc')) {
      return 'fa-check-square-o';
    }

    if (lowerName.includes('hsr')) {
      return 'fa-user-md';
    }

    return 'fa-graduation-cap';
  }

  private getCategoryImage(name: string, index: number): string {
    const lowerName = (name || '').toLowerCase();

    if (lowerName.includes('high')) {
      return 'assets/images/categories/high-risk-training.jpg';
    }

    if (lowerName.includes('construction')) {
      return 'assets/images/categories/construction-skills.jpg';
    }

    if (lowerName.includes('emergency') || lowerName.includes('first')) {
      return 'assets/images/categories/emergency-services.jpg';
    }

    if (lowerName.includes('plant') || lowerName.includes('machinery')) {
      return 'assets/images/categories/plant-machinery.jpg';
    }

    if (lowerName.includes('voc')) {
      return 'assets/images/categories/voc.jpg';
    }

    if (lowerName.includes('hsr')) {
      return 'assets/images/categories/hsr.jpg';
    }

    const fallbackImages = [
      'assets/images/categories/high-risk-training.jpg',
      'assets/images/categories/construction-skills.jpg',
      'assets/images/categories/emergency-services.jpg',
      'assets/images/categories/plant-machinery.jpg',
      'assets/images/categories/voc.jpg',
      'assets/images/categories/hsr.jpg'
    ];

    return fallbackImages[index % fallbackImages.length];
  }


  /** Return the delivery option list used by the rich long-course popup. */
  /**
   * Builds the customer-facing qualification title in the same pattern used by
   * VICSEG New Futures (for example: "Certificate III in Individual Support").
   * Existing data that already includes the qualification level is preserved
   * without adding the prefix twice.
   */courseDisplayTitle(course: CourseModel): string {
    const name = (course?.courseName || '').trim();
    const type = (course?.qualificationType || '').trim();

    // Remove "None" qualification type
    if (!type || type.toLowerCase() === 'none') {
      return name;
    }

    if (!name) return type;

    if (name.toLowerCase().startsWith(type.toLowerCase())) {
      return name;
    }

    const connector = /^(diploma|advanced diploma)$/i.test(type) ? ' of ' :
      /^(certificate\s+[ivx]+)$/i.test(type) ? ' in ' : ' ';

    return `${type}${connector}${name}`.replace(/\s+/g, ' ').trim();
  }


  deliveryOptionsFor(course: CourseModel): string[] {
    if (course.deliveryOptions?.length) return course.deliveryOptions.filter(value => !!value?.trim());
    return (course.deliveryMode || '').split(/[,\n/]+/).map(value => value.trim()).filter(Boolean);
  }

  /** Return the structured prerequisite list saved by the admin portal. */
  prerequisitesFor(course: CourseModel): string[] {
    return (course.coursePrerequisites || []).filter(value => !!value?.trim());
  }

  /** Return only valid relational pathways that can navigate to another course. */
  pathwaysFor(course: CourseModel) {
    return (course.studyPathways || []).filter(
      pathway => Number(pathway?.relatedCourseId) > 0 && !!pathway?.courseName?.trim()
    );
  }

  /** Return all active course units. */
  activeUnitsFor(course: CourseModel): CourseUnitModel[] {
    return (course.courseUnits || []).filter(unit => unit.isActive !== false);
  }

  /** Return active core units in their saved sort order. */
  coreUnitsFor(course: CourseModel): CourseUnitModel[] {
    return this.activeUnitsFor(course)
      .filter(unit => (unit.unitType || 'core') === 'core')
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }

  /** Return active elective units in their saved sort order. */
  electiveUnitsFor(course: CourseModel): CourseUnitModel[] {
    return this.activeUnitsFor(course)
      .filter(unit => unit.unitType === 'elective')
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }

  openCoursePopup(course: CourseModel): void {
    // Set selected course for popup.
    this.popupCourse = course;

    // Display popup.
    this.displayPopup = true;
    this.courseService.getCourseById(course.courseId).subscribe({
      next: detail => {
        if (detail?.courseId === course.courseId && this.popupCourse?.courseId === course.courseId) {
          this.popupCourse = { ...course, ...detail };
          if (Array.isArray(detail.availableClusters)) {
            this.loadedOffers.set(course.courseId, detail.availableClusters);
            this.offersErrors.delete(course.courseId);
          }
        }
      },
      error: () => { if (this.popupCourse?.courseId === course.courseId) this.message = 'Additional course information could not be loaded. Please try again.'; }
    });
  }

  closeCoursePopup(): void {
    // Hide popup.
    this.displayPopup = false;

    // Clear selected popup course.
    this.popupCourse = null;
  }

  /**
   * Adds the course currently displayed in the details popup and opens the cart.
   * Existing cart items are not duplicated; selecting the CTA again simply takes
   * the customer back to the cart view.
   */
  addPopupCourseToCartAndViewCart(): void {
    if (this.popupCourse) this.addPublicCourse(this.popupCourse);
  }

  bookNowFromPopup(): void {
    if (this.popupCourse) this.addPublicCourse(this.popupCourse);
  }

  // ============================================================
  // COURSE TITLE
  // ============================================================

  // Get course title
  getCourseTitle(course: CourseModel): string {
    // Return UOC title
    return course?.courseName || '';
  }


  // ============================================================
  // COURSE DESCRIPTION
  // ============================================================

  // Get course description
  getCourseDescription(course: any): string {

    const description = course?.description || '';

    if (!description.trim()) {
      return '-';
    }

    const container = document.createElement('div');
    container.innerHTML = description;

    const plainText = (container.textContent || '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const maxLength = 250;

    return plainText.length > maxLength
      ? plainText.substring(0, maxLength).trimEnd() + '...'
      : plainText;
  }
  // ============================================================
  // COURSE IMAGE
  // ============================================================

  // Check course image
  hasCourseImage(course: CourseModel): boolean {
    return !!(course?.imageUrl && course.imageUrl.trim().length > 0);
  }

  // Get course code
  getCourseCode(course: CourseModel): string {

    // Return UOC code
    return course?.courseCode || 'UOC';
  }

  // Get course price
  getPrice(course: CourseModel): string {

    // Check price availability
    if (!this.hasPrice(course)) {
      return 'Enquire for pricing';
    }

    // Store currency code
    const currency = course?.currencyCode || 'AUD';
    // Format course price
    return new Intl.NumberFormat(
      'en-AU',
      {
        style: 'currency',
        currency: currency
      }
    ).format(Number(course.price));
  }

  // Check course price availability
  hasPrice(course: CourseModel): boolean {
    return (Number(course?.price ?? 0) > 0
    );
  }

 

courseDropdownPageSize = 50;
courseDropdownTotalRecords = 0;
courseDropdownSearchText = '';
courseDropdownLoading = false;
courseDropdownHasLoaded = false;

private courseDropdownCurrentPage = 0;
private courseDropdownRequestVersion = 0;

private courseDropdownSearchTimer:
  ReturnType<typeof setTimeout> | null = null;

private courseDropdownScrollElement: HTMLElement | null = null;

private courseDropdownScrollHandler = (): void => {
  const el = this.courseDropdownScrollElement;

  if (
    !el ||
    el.scrollTop + el.clientHeight <
      el.scrollHeight - 35
  ) {
    return;
  }

  this.onCourseOptionsScroll();
};

// ============================================================
// INITIALISE QUALIFICATION DROPDOWN
// ============================================================

loadCourseOptions(): void {

  this.courseDropdownRequestVersion++;

  this.courseOptions = [];
  this.courseDropdownTotalRecords = 0;
  this.courseDropdownCurrentPage = 0;
  this.courseDropdownLoading = false;
  this.courseDropdownHasLoaded = false;

  this.loadCourseOptionsPage(1);
}


// ============================================================
// LOAD QUALIFICATIONS FROM EXISTING PAGINATED API
// ============================================================

private loadCourseOptionsPage(page: number): void {

  if (page < 1 || this.courseDropdownLoading) {
    return;
  }

  if (
    this.courseDropdownHasLoaded &&
    (
      this.courseOptions.length >= this.courseDropdownTotalRecords ||
      page <= this.courseDropdownCurrentPage
    )
  ) {
    return;
  }

  const requestVersion = this.courseDropdownRequestVersion;

  this.courseDropdownLoading = true;

  this.courseService.getCourses(
    this.selectedTagSlug,
    page,
    this.courseDropdownPageSize,
    this.selectedQualificationStateCode,
    {
      keyword: this.courseDropdownSearchText
    }
  ).subscribe({

    next: (response: any) => {

      if (
        requestVersion !== this.courseDropdownRequestVersion
      ) {
        return;
      }

      const records = response?.records || [];

      this.courseDropdownTotalRecords =
        Number(response?.totalRecords || 0);

      const existingIds = new Set(
        this.courseOptions.map(
          course => Number(course.courseId)
        )
      );

      const newRecords = records
        .map((course: any) => ({
          ...course,
          courseId: Number(course.courseId ?? course.id),
          courseCode: course.courseCode || '',
          courseName: course.courseName ?? course.courseTitle ?? ''
        }))
        .filter((course: any) =>
          course.courseId > 0 &&
          !existingIds.has(course.courseId)
        );

      this.courseOptions = [
        ...this.courseOptions,
        ...newRecords
      ];

      this.courseDropdownCurrentPage = page;
      this.courseDropdownHasLoaded = true;
      this.courseDropdownLoading = false;
    },

    error: (error) => {

      if (
        requestVersion !== this.courseDropdownRequestVersion
      ) {
        return;
      }

      console.error(
        'Error loading qualification options:',
        error
      );

      this.courseDropdownLoading = false;
    }
  });
}


// ============================================================
// LOAD NEXT 50 QUALIFICATIONS
// ============================================================

onCourseOptionsScroll(): void {

  if (
    this.courseDropdownLoading ||
    !this.courseDropdownHasLoaded ||
    this.courseOptions.length >= this.courseDropdownTotalRecords
  ) {
    return;
  }

  this.loadCourseOptionsPage(
    this.courseDropdownCurrentPage + 1
  );
}


// ============================================================
// SERVER-SIDE SEARCH
// ============================================================

onCourseOptionsSearch(value: string): void {

  if (this.courseDropdownSearchTimer) {
    clearTimeout(this.courseDropdownSearchTimer);
  }

  this.courseDropdownSearchTimer = setTimeout(() => {

    this.courseDropdownSearchText = value.trim();

    this.loadCourseOptions();

  }, 350);
}


// ============================================================
// DROPDOWN OPEN
// ============================================================

onCourseOptionsOpened(): void {

  if (
    !this.courseDropdownHasLoaded &&
    !this.courseDropdownLoading
  ) {
    this.loadCourseOptionsPage(1);
  }

  setTimeout(() => {

    this.onCourseOptionsClosed();

    const panel = document.querySelector(
      '.ecb-qualification-dropdown-panel'
    );

    this.courseDropdownScrollElement =
      panel?.querySelector(
        '.p-dropdown-items-wrapper'
      ) as HTMLElement | null;

    this.courseDropdownScrollElement?.addEventListener(
      'scroll',
      this.courseDropdownScrollHandler
    );

  }, 0);
}


// ============================================================
// DROPDOWN CLOSE
// ============================================================

onCourseOptionsClosed(): void {

  this.courseDropdownScrollElement?.removeEventListener(
    'scroll',
    this.courseDropdownScrollHandler
  );

  this.courseDropdownScrollElement = null;
}

}
